import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Card, Button } from 'antd';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import settings from 'front-settings';

import { updateConfigValue } from 'modules/appConfig';
import { getIntersectionsZone, updateRequestProperties } from 'modules/userrequest';
import { getFeatureWithProperties, getActivityFeatures } from 'helpers/mapHelpers';
import { getFeatureById } from 'helpers/userrequestHelpers';
import FeaturesList from 'components/FormMap/FeatureList';
import TerraDrawMap from 'components/TerraDrawMap/TerraDrawMap';
import { terraDrawMapConfig, mapTitleLegend } from 'components/FormMap/FormMap.config';

const mapboxStyleIGN = {
  version: 8,
  sources: {
    'raster-tiles': {
      type: 'raster',
      tileSize: 256,
      tiles: [settings.BASE_RASTERLAYER],
    },
  },
  layers: [{ id: 'background', type: 'raster', source: 'raster-tiles' }],
};

class FormMap extends Component {
  state = {
    selectedFeaturesId: [],
    showGeojsonConflicts: false,
  };

  onSelectionChange = e => {
    this.setState({ selectedFeaturesId: e.features.map(feature => feature.id) });
  }

  onConflictsChange = () => {
    this.setState(previousState => ({ showGeojsonConflicts: !previousState.showGeojsonConflicts }));
  }

  /**
   * updateZoneWhenDeleteDataDraw clean zone properties when there is no data
   * @param {string} activity of the feature
   *
   * @memberof FormMap
   */
  updateZoneWhenDeleteDataDraw = activity => {
    const { activities, features } = this.props;
    const getFeaturesByActivity = features.reduce((list, { properties, zone = false }) => (
      (properties.activity !== activity && !zone)
        ? list
        : [...list, ...zone]
    ), []);
    this.props.updateRequestProperties({
      activities: activities.map(item => (
        (item.uid !== activity)
          ? item
          : {
            ...item,
            zone: getFeaturesByActivity || [],
          }
      )),
    });
  }

  setRef = el => {
    this.mapContainer = el;
    return this;
  }

  /**
   * handleUpdateDataDraw add or update features
   * - get all needed properties (dates, activity id, timestamp, etc).
   * - call intersection request to get incidence
   * @param {array} features
   *
   * @memberof FormMap
   */
  handleUpdateDataDraw = features => {
    features.forEach(feature => {
      const { activity: { uid } } = this.props;
      const featureWithProperties = getFeatureWithProperties(feature, uid);
      this.props.updateFeatures(featureWithProperties);
    });
  }

  /**
   * Handle on delete feature(s) on map
   * - delete feature from TerraDrawMap
   * - call props function onDeleteDataDraw
   * - Filter selectedFeaturesId from state to remove deleted features
   * @param {array} features array of features deleted by TerraDrawMap
   *
   * @memberof FormMap
   */
  handleDeleteDataDraw = features => {
    const { relatedFeature, featuresId } = features.reduce((acc, feature) => ({
      relatedFeature: feature.properties.relatedFeatureId
        ? getFeatureById(this.props.features, feature.properties.relatedFeatureId)
        : acc.relatedFeatures,
      featuresId: [...acc.featuresId, feature.id],
    }), { relatedFeature: null, featuresId: [] });

    const [{ properties: { activity } }] = this.props.features.filter(f => f.id === featuresId[0]);

    this.props.deleteFeaturesById(featuresId);
    if (relatedFeature) {
      this.mapContainer.deleteFeatureById(relatedFeature.properties.id);
    }

    this.updateZoneWhenDeleteDataDraw(activity);

    this.setState({ selectedFeaturesId: [] });
  }

  /**
   * Handle on click on feature list item
   * @param {string} featureId
   *
   * @memberof FormMap
   */
  deleteDrawData = id => {
    const [feature] = this.props.features.filter(f => f.id === id);
    this.props.deleteFeaturesById([id]);
    this.mapContainer.deleteFeatureById(id);
    setTimeout(() => {
      feature && this.updateZoneWhenDeleteDataDraw(feature.properties.activity);
    });
    this.setState({
      selectedFeaturesId: [],
    });
  }

  render () {
    const {
      features,
      editable,
      activity,
      activityFilters,
      geojsonConflicts,
      activityDates,
      onlyMap,
      mapProps,
      mapSettings,
      deleteFeaturesById,
    } = this.props;

    const activityFeatures = getActivityFeatures(features, activity.uid);
    const featureList = activityFeatures.filter(feature => !feature.properties.routeInProgress);

    const mapDrawerProps = { expandOnInit: editable };
    return (
      <Row gutter={24} style={{ paddingBottom: 24 }}>
        <Col span={24} lg={24} style={{ height: 450, overflow: 'hidden', position: 'relative' }}>
          <TerraDrawMap
            geojsonConflicts={geojsonConflicts}
            showGeojsonConflicts={this.state.showGeojsonConflicts}
            mapboxAccessToken={settings.MAPBOX_ACCESS_TOKEN}
            features={activityFeatures}
            config={terraDrawMapConfig}
            deleteFeaturesById={deleteFeaturesById}
            activityFilters={activityFilters}
            activityDates={activityDates}
            onUpdateDataDraw={this.handleUpdateDataDraw}
            onDeleteDataDraw={this.handleDeleteDataDraw}
            onSelectionChange={this.onSelectionChange}
            editable={editable}
            mapDrawerProps={mapDrawerProps}
            mapProps={mapProps}
            ref={this.setRef}
            {...mapSettings}
            onlyMap={onlyMap}
            mapboxStyle={onlyMap ? mapSettings.mapboxStyle : mapboxStyleIGN}
          />
        </Col>
        {!onlyMap && (
          <Col span={24} lg={24}>
            <Card
              title={(
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between', width: '100%' }}>
                  <div>{mapTitleLegend.title}</div>
                  {geojsonConflicts.features.length > 0 && (
                    <Button onClick={this.onConflictsChange}>
                      Afficher les conflits avec d'autres activités
                    </Button>
                  )}
                </div>
              )}
            >
              <FeaturesList
                activityDates={activityDates}
                filters={activityFilters}
                activity={activity}
                features={featureList}
                selectedFeaturesId={this.state.selectedFeaturesId}
                deleteFeatureById={this.deleteDrawData}
                editable={editable}
                updateFeatureName={this.props.updateFeatureName}
              />
            </Card>
          </Col>
        )}
      </Row>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  features: ownProps.features || state.userrequest.geojson.features,
  mapSettings: state.appConfig.mapSettings,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({
    updateConfigValue,
    updateRequestProperties,
    getIntersectionsZone,
  }, dispatch);

FormMap.propTypes = {
  updateFeatures: PropTypes.func,
  updateFeatureName: PropTypes.func,
  deleteFeaturesById: PropTypes.func,
  geojsonConflicts: PropTypes.object,
  editable: PropTypes.bool,
  activity: PropTypes.shape({
    type: PropTypes.string,
    eventDates: PropTypes.array,
    uid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    participantCount: PropTypes.string,
    publicCount: PropTypes.string,
  }),
  features: PropTypes.arrayOf(PropTypes.shape({
    properties: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    geometry: PropTypes.object,
  })),
  mapProps: PropTypes.object,
  onlyMap: PropTypes.bool,
  activities: PropTypes.array,
};

FormMap.defaultProps = {
  geojsonConflicts: {
    features: [],
  },
  updateFeatures: () => {},
  updateFeatureName: () => {},
  deleteFeaturesById: () => {},
  features: [],
  activity: {
    uid: 0,
  },
  activities: [],
  editable: false,
  mapProps: {},
  onlyMap: false,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormMap);



// WEBPACK FOOTER //
// ./src/components/FormMap/FormMap.js